import { useMemo } from "react";
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Build as BuildIcon,
  AddCircleOutline as AddIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Speed as SpeedIcon,
  Power as OnlineIcon,
} from "@mui/icons-material";
import type { MenuOptionType } from "@/controls/contextMenu/models/MenuOptionType";
import {
  PART_IDS,
  isSpareContainer,
  isShellType,
  type SidewallTreeRow,
} from "../models";

interface UseSidewallContextMenuProps {
  onInstall: (row: SidewallTreeRow) => void;
  onUninstall: (row: SidewallTreeRow) => void;
  onRepair: (row: SidewallTreeRow) => void;
  onNewSparePart: (row: SidewallTreeRow) => void;
  onEditPart: (row: SidewallTreeRow) => void;
  onSetInitialMetric: (row: SidewallTreeRow) => void;
  onCorrectDates: (row: SidewallTreeRow) => void;
  onSetOnline: (row: SidewallTreeRow) => void;
  onSetOffline: (row: SidewallTreeRow) => void;
}

// Top-level containers that should not show context menu
const EXCLUDED_CONTAINERS: number[] = [
  PART_IDS.SIDEWALL,
  PART_IDS.SPARES,
  PART_IDS.SHELL_SPARES,
  PART_IDS.SHELL_PARTS_SPARES,
];

export const useSidewallContextMenu = ({
  onInstall,
  onUninstall,
  onRepair,
  onNewSparePart,
  onEditPart,
  onSetInitialMetric,
  onCorrectDates,
  onSetOnline,
  onSetOffline,
}: UseSidewallContextMenuProps) => {
  const getMenuOptions = useMemo(
    () => (data: SidewallTreeRow) => {
      const options: MenuOptionType[] = [];

      if (!data) return options;

      // Slot containers (Upper Location, Lower Location) — permanent, like Caster Strands
      // Show "Set Online" when empty (no shell installed)
      if (data.rowType === "container" && data.SlotPartTypeId != null) {
        if (!data.HasShellInstalled) {
          options.push({
            name: "Set Online",
            key: "setOnline",
            onClick: () => onSetOnline(data),
            icon: <OnlineIcon fontSize="small" color="success" />,
          });
        }
        return options;
      }

      // Context menu for spares containers and spare parts
      if (data.rowType === "sparesContainer" || data.rowType === "sparePart") {
        // Exclude top-level containers
        const containerId = data.ContainerId ?? data.EquipmentId;
        if (EXCLUDED_CONTAINERS.includes(containerId)) {
          return options;
        }

        // Only show "New Spare Part" for actual spares containers (not top-level)
        if (isSpareContainer(containerId)) {
          options.push({
            name: "New Spare Part",
            key: "newSparePart",
            onClick: () => onNewSparePart(data),
            icon: <AddIcon fontSize="small" color="primary" />,
          });
        }

        // Add options for spare parts
        if (data.rowType === "sparePart" && data.L2PartId) {
          options.push({
            name: "Edit Part",
            key: "editPart",
            onClick: () => onEditPart(data),
            icon: <EditIcon fontSize="small" color="primary" />,
          });
          options.push({
            name: "Repair",
            key: "repair",
            onClick: () => onRepair(data),
            icon: <BuildIcon fontSize="small" color="warning" />,
          });
          options.push({
            name: "Set Initial Metric",
            key: "setInitialMetric",
            onClick: () => onSetInitialMetric(data),
            icon: <SpeedIcon fontSize="small" color="info" />,
          });
          options.push({
            name: "Correct Dates",
            key: "correctDates",
            onClick: () => onCorrectDates(data),
            icon: <ScheduleIcon fontSize="small" color="primary" />,
          });
        }

        return options;
      }

      // Context menu for location rows (panel/component positions)
      if (data.rowType !== "location") {
        return options;
      }

      // Shell rows (Upper Shell, Lower Shell) — set online/offline is done via power button.
      // Context menu shows only Set Initial Metric and Correct Dates (like Roof).
      const isShellRow =
        isShellType(data.PartTypeId) && data.HasChildren === true;

      if (isShellRow) {
        options.push({
          name: "Set Initial Metric",
          key: "setInitialMetric",
          onClick: () => onSetInitialMetric(data),
          icon: <SpeedIcon fontSize="small" color="info" />,
        });
        options.push({
          name: "Correct Dates",
          key: "correctDates",
          onClick: () => onCorrectDates(data),
          icon: <ScheduleIcon fontSize="small" color="primary" />,
        });
        return options;
      }

      const hasPart = data.HasAssignedPart ?? false;
      const hasBurnerChildren = data.HasChildren === true;

      if (!hasPart && !hasBurnerChildren) {
        // Show "Install Part" option for empty positions without sub-components
        // Burner positions (3, 5, 9) have HasChildren=true - parts are installed on sub-positions
        // Sub-positions (Block, etc.) have HasChildren=false and use BurnerPanelPartId as parent
        options.push({
          name: "Install Part",
          key: "install",
          onClick: () => onInstall(data),
          icon: <LinkIcon fontSize="small" color="primary" />,
        });
      } else if (hasPart) {
        // Show options for positions with parts
        options.push({
          name: "Uninstall Part",
          key: "uninstall",
          onClick: () => onUninstall(data),
          icon: <LinkOffIcon fontSize="small" color="error" />,
        });
        options.push({
          name: "Repair",
          key: "repair",
          onClick: () => onRepair(data),
          icon: <BuildIcon fontSize="small" color="warning" />,
        });
        options.push({
          name: "Edit Part",
          key: "editPart",
          onClick: () => onEditPart(data),
          icon: <EditIcon fontSize="small" color="primary" />,
        });
        options.push({
          name: "Set Initial Metric",
          key: "setInitialMetric",
          onClick: () => onSetInitialMetric(data),
          icon: <SpeedIcon fontSize="small" color="info" />,
        });
        options.push({
          name: "Correct Dates",
          key: "correctDates",
          onClick: () => onCorrectDates(data),
          icon: <ScheduleIcon fontSize="small" color="primary" />,
        });
      }

      return options;
    },
    [
      onInstall,
      onUninstall,
      onRepair,
      onNewSparePart,
      onEditPart,
      onSetInitialMetric,
      onCorrectDates,
      onSetOnline,
      onSetOffline,
    ],
  );

  return {
    getMenuOptions,
  };
};
